import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import FilterListIcon from "@material-ui/icons/FilterList";
import SearchIcon from "@material-ui/icons/Search";

import { FB } from "../../assets/feedbackTheme";

// Column-header checkbox filter for the İçerik Takip table. Same value semantics
// and select-all UX as AchievementReactions/DropdownFilter, but triggered from a
// small icon in a (teal) table header and rendered position:fixed so the table's
// scroll container can't clip it.
//
// `options` is [{ value, label }]: matching uses value, the list shows label
// (e.g. Ders filters on CourseId but shows the course name).
//
// Filter value semantics:
//   null      → all selected (no filter applied)
//   []        → none checked (0 rows pass)
//   ["A","B"] → only those values pass

const POPOVER_WIDTH = 260;

const styles = {
  trigger: (active) => ({
    position: "relative",
    width: 22,
    height: 22,
    flex: "0 0 auto",
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    backgroundColor: active ? FB.surface : "transparent",
    color: active ? FB.accent : FB.surface,
    opacity: active ? 1 : 0.75,
  }),
  badge: (empty) => ({
    position: "absolute",
    top: -6,
    right: -7,
    minWidth: 15,
    height: 15,
    padding: "0 4px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "15px",
    textAlign: "center",
    color: FB.surface,
    backgroundColor: empty ? FB.danger : FB.accentMid,
    border: `1px solid ${FB.surface}`,
    boxSizing: "border-box",
  }),
  popover: {
    position: "fixed",
    zIndex: 1300,
    width: POPOVER_WIDTH,
    maxHeight: 340,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: FB.surface,
    border: `1px solid ${FB.border}`,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    color: FB.text,
    fontWeight: 400,
    textTransform: "none",
    letterSpacing: 0,
  },
  searchWrap: {
    position: "relative",
    padding: "10px 10px 6px",
    flexShrink: 0,
  },
  searchIcon: {
    position: "absolute",
    left: 18,
    top: "50%",
    transform: "translateY(-30%)",
    fontSize: 16,
    color: FB.textMuted,
  },
  searchInput: {
    width: "100%",
    height: 30,
    boxSizing: "border-box",
    padding: "0 10px 0 28px",
    fontSize: 12,
    color: FB.text,
    border: `1px solid ${FB.border}`,
    borderRadius: 7,
    outline: "none",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "6px 12px 8px",
    borderBottom: `1px solid ${FB.border}`,
    flexShrink: 0,
  },
  list: { overflowY: "auto", padding: "4px 0" },
  option: (hovered) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "6px 12px",
    fontSize: 12,
    lineHeight: 1.35,
    cursor: "pointer",
    backgroundColor: hovered ? FB.accentSoft : "transparent",
    whiteSpace: "normal",
    wordBreak: "break-word",
  }),
  selectAll: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  clearBtn: {
    border: "none",
    background: "none",
    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    color: FB.danger,
    cursor: "pointer",
  },
  checkbox: { margin: "1px 0 0", accentColor: FB.accent, cursor: "pointer" },
  empty: { padding: "12px", fontSize: 12, color: FB.textMuted, textAlign: "center" },
};

const HeaderFilter = ({ label, options, selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const isAllSelected = selectedValues == null;
  const isFiltered = !isAllSelected;
  const isChecked = (val) => isAllSelected || selectedValues.includes(val);

  const values = useMemo(() => options.map((o) => o.value), [options]);

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return options;
    return options.filter((o) => o.label.toLocaleLowerCase("tr-TR").includes(q));
  }, [options, query]);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left - 8, window.innerWidth - POPOVER_WIDTH - 8)),
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const inside = (target) =>
      (popoverRef.current && popoverRef.current.contains(target)) ||
      (triggerRef.current && triggerRef.current.contains(target));
    const onMouseDown = (e) => {
      if (!inside(e.target)) setIsOpen(false);
    };
    // The popover is fixed-positioned, so close it when anything outside scrolls.
    const onScroll = (e) => {
      if (!inside(e.target)) setIsOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const close = () => setIsOpen(false);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("scroll", onScroll, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const toggleSelectAll = () => onChange(isAllSelected ? [] : null);

  const toggleValue = (val) => {
    if (isAllSelected) {
      onChange(values.filter((v) => v !== val));
    } else if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      const next = [...selectedValues, val];
      onChange(next.length === values.length ? null : next);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        title={`${label} filtrele`}
        aria-label={`${label} filtrele`}
        aria-expanded={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((o) => !o);
        }}
        style={styles.trigger(isFiltered)}
      >
        <FilterListIcon style={{ fontSize: 15 }} />
        {isFiltered && (
          <span style={styles.badge(selectedValues.length === 0)}>{selectedValues.length}</span>
        )}
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          style={{ ...styles.popover, top: position.top, left: position.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.length > 6 && (
            <div style={styles.searchWrap}>
              <SearchIcon style={styles.searchIcon} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Değer ara..."
                style={styles.searchInput}
              />
            </div>
          )}

          <div style={styles.head}>
            <label style={styles.selectAll}>
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={isAllSelected}
                ref={(el) => {
                  if (el) el.indeterminate = isFiltered && selectedValues.length > 0;
                }}
                onChange={toggleSelectAll}
              />
              Tümünü seç
            </label>
            {isFiltered && (
              <button type="button" style={styles.clearBtn} onClick={() => onChange(null)}>
                Temizle
              </button>
            )}
          </div>

          <div style={styles.list}>
            {visibleOptions.length === 0 ? (
              <div style={styles.empty}>Değer bulunamadı</div>
            ) : (
              visibleOptions.map(({ value, label: optionLabel }) => (
                <label
                  key={String(value)}
                  style={styles.option(hovered === value)}
                  onMouseEnter={() => setHovered(value)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={isChecked(value)}
                    onChange={() => toggleValue(value)}
                  />
                  <span>{optionLabel}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderFilter;
